/**
 * Predicted track + true-altitude rendering (VLP-2300 WP-B).
 *
 * Two halves:
 *  - Pure builders (unit-tested): the forecast ribbon / σ cone / time ticks as GeoJSON for the 2D map,
 *    and the same geometry as small triangle meshes (lng, lat, metres) for 3D.
 *  - `Track3dLayer` (track3dLayer.ts): a MapLibre CustomLayerInterface that draws those meshes at real heights. MapLibre v5
 *    ignores z in line/fill coordinates, so the elevated aircraft, its drop line to the ground, the
 *    elevated ribbon and cone are drawn here in mercator space (MercatorCoordinate.fromLngLat(…, altM)),
 *    relative to a local origin so float32 precision holds at street zoom.
 *
 * Provenance (aviation data spec §2 / §11): every forecast is class E — always visually distinct and
 * always drawn with its cone. 'solid' tier = solid ribbon, 'projected' = dashed. Suppressed (stale/lost)
 * = nothing drawn, and the map says why.
 */
import { confidenceTier, M_PER_FT, type AircraftState, type TrackSample } from '@apos/shared';
import { fmt } from '../../lib/fmt';

type LngLat = [number, number];

/** Time ticks along the ribbon (s ahead). */
export const TICK_SECONDS = [10, 20, 30, 60] as const;
/** Old flat leader parity: below this ground speed (kt) a hover / taxi draws no forecast. */
export const RIBBON_MIN_GS_KT = 20;
/** Horizon per role: Subject / Chase 2 min, Watch 1 min (the old leader's lengths). */
export function ribbonHorizonSec(kind: 'subject' | 'chase' | 'watch'): number {
  return kind === 'watch' ? 60 : 120;
}

const M_PER_DEG = 111_320;

function offsetM(lat: number, lon: number, eastM: number, northM: number): LngLat {
  const c = Math.max(0.01, Math.cos((lat * Math.PI) / 180));
  return [lon + eastM / (M_PER_DEG * c), lat + northM / M_PER_DEG];
}

/** Local bearing (rad, clockwise from north) between two samples, flat-earth (segments are short). */
function headingRad(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const c = Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.atan2((b.lon - a.lon) * c, b.lat - a.lat);
}

/** Heading at sample i (central difference; ends use their one neighbour). */
function headingAt(samples: readonly { lat: number; lon: number }[], i: number): number | null {
  const a = samples[Math.max(0, i - 1)]!;
  const b = samples[Math.min(samples.length - 1, i + 1)]!;
  if (a === b || (a.lat === b.lat && a.lon === b.lon)) return null;
  return headingRad(a, b);
}

/**
 * Re-anchor a forecast computed at the world state's time to what is on screen: drop the `aheadSec`
 * already dead-reckoned by the display, renumber tSec from the display instant, and shift so sample 0
 * sits exactly on the displayed aircraft (the spring glide's small offset).
 */
export function rebaseSamples(samples: readonly TrackSample[], aheadSec: number, disp: { lat: number; lon: number }): TrackSample[] {
  if (samples.length === 0) return [];
  const ahead = Math.max(0, Number.isFinite(aheadSec) ? aheadSec : 0);
  const out: TrackSample[] = [];
  // the sample at `ahead` (interpolated)
  let k = 0;
  while (k < samples.length - 1 && samples[k + 1]!.tSec <= ahead) k++;
  const a = samples[k]!;
  const b = samples[Math.min(samples.length - 1, k + 1)]!;
  const f = b.tSec > a.tSec ? Math.min(1, Math.max(0, (ahead - a.tSec) / (b.tSec - a.tSec))) : 0;
  const lerp = (x: number, y: number) => x + (y - x) * f;
  const head: TrackSample = {
    tSec: 0,
    lat: lerp(a.lat, b.lat),
    lon: lerp(a.lon, b.lon),
    altM: a.altM != null && b.altM != null ? lerp(a.altM, b.altM) : a.altM,
    sigmaHM: lerp(a.sigmaHM, b.sigmaHM),
    sigmaVM: a.sigmaVM != null && b.sigmaVM != null ? lerp(a.sigmaVM, b.sigmaVM) : a.sigmaVM,
    tier: f > 0 ? b.tier : a.tier,
  };
  const dLat = disp.lat - head.lat;
  const dLon = disp.lon - head.lon;
  out.push({ ...head, lat: disp.lat, lon: disp.lon });
  for (let i = k + 1; i < samples.length; i++) {
    const s = samples[i]!;
    if (s.tSec <= ahead) continue;
    out.push({ ...s, tSec: s.tSec - ahead, lat: s.lat + dLat, lon: s.lon + dLon });
  }
  return out;
}

/** Interpolated sample at tSec (null outside the forecast). */
export function sampleAt(samples: readonly TrackSample[], tSec: number): TrackSample | null {
  if (samples.length === 0 || tSec < samples[0]!.tSec || tSec > samples[samples.length - 1]!.tSec) return null;
  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i]!;
    const b = samples[i + 1]!;
    if (tSec >= a.tSec && tSec <= b.tSec) {
      const f = b.tSec > a.tSec ? (tSec - a.tSec) / (b.tSec - a.tSec) : 0;
      return {
        tSec,
        lat: a.lat + (b.lat - a.lat) * f,
        lon: a.lon + (b.lon - a.lon) * f,
        altM: a.altM != null && b.altM != null ? a.altM + (b.altM - a.altM) * f : a.altM,
        sigmaHM: a.sigmaHM + (b.sigmaHM - a.sigmaHM) * f,
        sigmaVM: a.sigmaVM,
        tier: f > 0 ? b.tier : a.tier,
      };
    }
  }
  return samples[samples.length - 1]!;
}

/**
 * The σ cone: ±sigmaHM perpendicular to the track at every sample, closed ring (left edge out, right
 * edge back). Null with fewer than two samples.
 */
export function coneRing(samples: readonly TrackSample[]): LngLat[] | null {
  if (samples.length < 2) return null;
  const left: LngLat[] = [];
  const right: LngLat[] = [];
  let last = 0;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    const h = headingAt(samples, i) ?? last;
    last = h;
    const r = Math.max(0, s.sigmaHM);
    // perpendicular: heading ± 90°
    const ex = Math.cos(h) * r;
    const ny = -Math.sin(h) * r;
    left.push(offsetM(s.lat, s.lon, -ex, -ny));
    right.push(offsetM(s.lat, s.lon, ex, ny));
  }
  const ring = [...left, ...right.reverse()];
  ring.push(ring[0]!);
  return ring;
}

/** Contiguous runs of the same tier; consecutive runs share their boundary sample so the line is unbroken. */
export function tierRuns(samples: readonly TrackSample[]): Array<{ tier: TrackSample['tier']; coords: LngLat[] }> {
  const runs: Array<{ tier: TrackSample['tier']; coords: LngLat[] }> = [];
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    const cur = runs[runs.length - 1];
    if (!cur || (cur.tier !== s.tier && i > 0)) {
      const prev = samples[i - 1];
      runs.push({ tier: s.tier, coords: prev ? [[prev.lon, prev.lat]] : [] });
    }
    runs[runs.length - 1]!.coords.push([s.lon, s.lat]);
  }
  return runs.filter((r) => r.coords.length >= 2);
}

/**
 * 2D GeoJSON for one aircraft's forecast: ribbon runs (k 'ribbon', tier), the cone (k 'cone') — always
 * together — and time ticks (k 'tick', s = seconds ahead). Empty when there is nothing to draw.
 */
export function trackFeatures(id: string, samples: readonly TrackSample[], color: string): GeoJSON.Feature[] {
  const runs = tierRuns(samples);
  const ring = coneRing(samples);
  if (runs.length === 0 || !ring) return [];
  const f: GeoJSON.Feature[] = [{ type: 'Feature', properties: { k: 'cone', id, c: color }, geometry: { type: 'Polygon', coordinates: [ring] } }];
  for (const r of runs) f.push({ type: 'Feature', properties: { k: 'ribbon', id, c: color, tier: r.tier }, geometry: { type: 'LineString', coordinates: r.coords } });
  for (const t of TICK_SECONDS) {
    const s = sampleAt(samples, t);
    if (!s) continue;
    f.push({ type: 'Feature', properties: { k: 'tick', id, c: color, s: t, tier: s.tier }, geometry: { type: 'Point', coordinates: [s.lon, s.lat] } });
  }
  return f;
}

/** "12 s" / "3 min" */
function ageText(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

/**
 * Why no forecast is drawn for a moving aircraft whose position is too old ("Forecast off: position
 * 42 s old"); null when a forecast is drawn or the aircraft simply is not moving / has no track.
 */
export function forecastOffText(s: Pick<AircraftState, 'ageSec' | 'freshness' | 'gsKt' | 'trackDeg' | 'lowIntegrity'> | null | undefined): string | null {
  if (!s || s.gsKt == null || s.gsKt <= RIBBON_MIN_GS_KT || s.trackDeg == null) return null;
  if (confidenceTier(s.ageSec, s.freshness, 0, { lowIntegrity: s.lowIntegrity }) !== 'suppressed') return null;
  if (s.ageSec == null || !Number.isFinite(s.ageSec)) return 'Forecast off: no position time';
  return `Forecast off: position ${ageText(s.ageSec)} old`;
}

// ───────────── 3D meshes ─────────────

/** Triangles: `pos` = lng, lat, metres (render height) per vertex; `col` = premultiplied RGBA 0..1 per vertex. */
export interface Mesh {
  pos: number[];
  col: number[];
}

export function emptyMesh(): Mesh {
  return { pos: [], col: [] };
}

export type Rgb = [number, number, number];

export function rgbOf(hex: string): Rgb {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function vert(m: Mesh, p: LngLat, z: number, c: Rgb, a: number): void {
  m.pos.push(p[0], p[1], z);
  m.col.push(c[0] * a, c[1] * a, c[2] * a, a);
}

function quad(m: Mesh, p0: LngLat, z0: number, p1: LngLat, z1: number, p2: LngLat, z2: number, p3: LngLat, z3: number, c: Rgb, a: number): void {
  vert(m, p0, z0, c, a);
  vert(m, p1, z1, c, a);
  vert(m, p2, z2, c, a);
  vert(m, p0, z0, c, a);
  vert(m, p2, z2, c, a);
  vert(m, p3, z3, c, a);
}

/** Triangle count of a mesh. */
export function triCount(m: Mesh): number {
  return m.pos.length / 9;
}

/**
 * Elevated ribbon along the samples (half-width in metres). Solid tier = continuous; projected tier =
 * dashed (every other sample interval drawn).
 */
export function ribbonMesh(m: Mesh, samples: readonly TrackSample[], zOf: (s: TrackSample) => number, halfWidthM: number, c: Rgb, alpha: number): void {
  let projectedIdx = 0;
  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i]!;
    const b = samples[i + 1]!;
    if (b.tier === 'projected' && projectedIdx++ % 2 === 1) continue;
    const h = headingRad(a, b);
    const ex = Math.cos(h) * halfWidthM;
    const ny = -Math.sin(h) * halfWidthM;
    const za = zOf(a);
    const zb = zOf(b);
    quad(m, offsetM(a.lat, a.lon, -ex, -ny), za, offsetM(b.lat, b.lon, -ex, -ny), zb, offsetM(b.lat, b.lon, ex, ny), zb, offsetM(a.lat, a.lon, ex, ny), za, c, alpha);
  }
}

/** Elevated σ cone: a horizontal band ±sigmaHM wide following the ribbon's heights. */
export function coneMesh(m: Mesh, samples: readonly TrackSample[], zOf: (s: TrackSample) => number, c: Rgb, alpha: number): void {
  let last = 0;
  const edges = samples.map((s, i) => {
    const h = headingAt(samples, i) ?? last;
    last = h;
    const ex = Math.cos(h) * s.sigmaHM;
    const ny = -Math.sin(h) * s.sigmaHM;
    return { l: offsetM(s.lat, s.lon, -ex, -ny), r: offsetM(s.lat, s.lon, ex, ny), z: zOf(s) };
  });
  for (let i = 0; i < edges.length - 1; i++) {
    const a = edges[i]!;
    const b = edges[i + 1]!;
    quad(m, a.l, a.z, b.l, b.z, b.r, b.z, a.r, a.z, c, alpha);
  }
}

/** Thin vertical drop line from the ground to the aircraft: two crossed vertical quads (visible from any bearing). */
export function dropLineMesh(m: Mesh, lon: number, lat: number, zGround: number, zAir: number, halfWidthM: number, c: Rgb, alpha: number): void {
  if (!(zAir > zGround)) return;
  const e1 = offsetM(lat, lon, -halfWidthM, 0);
  const e2 = offsetM(lat, lon, halfWidthM, 0);
  const n1 = offsetM(lat, lon, 0, -halfWidthM);
  const n2 = offsetM(lat, lon, 0, halfWidthM);
  quad(m, e1, zGround, e2, zGround, e2, zAir, e1, zAir, c, alpha);
  quad(m, n1, zGround, n2, zGround, n2, zAir, n1, zAir, c, alpha);
}

/** Aircraft glyph at altitude: a flat arrow along the track plus a small vertical fin (reads at low pitch too). */
export function glyphMesh(m: Mesh, lon: number, lat: number, z: number, trackDeg: number, sizeM: number, c: Rgb, alpha: number, outline?: Rgb): void {
  const h = (trackDeg * Math.PI) / 180;
  const at = (fwd: number, side: number): LngLat => {
    const e = Math.sin(h) * fwd + Math.cos(h) * side;
    const n = Math.cos(h) * fwd - Math.sin(h) * side;
    return offsetM(lat, lon, e * sizeM, n * sizeM);
  };
  const nose = at(1, 0);
  const lw = at(-0.75, -0.7);
  const notch = at(-0.4, 0);
  const rw = at(-0.75, 0.7);
  if (outline) {
    // a slightly larger dark arrow just under the glyph: separates it from any ground or sky
    const k = 1.28;
    const o = (fwd: number, side: number) => at(fwd * k, side * k);
    vert(m, o(1, 0), z - 0.5, outline, alpha);
    vert(m, o(-0.75, -0.7), z - 0.5, outline, alpha);
    vert(m, o(-0.4, 0), z - 0.5, outline, alpha);
    vert(m, o(1, 0), z - 0.5, outline, alpha);
    vert(m, o(-0.4, 0), z - 0.5, outline, alpha);
    vert(m, o(-0.75, 0.7), z - 0.5, outline, alpha);
  }
  vert(m, nose, z, c, alpha);
  vert(m, lw, z, c, alpha);
  vert(m, notch, z, c, alpha);
  vert(m, nose, z, c, alpha);
  vert(m, notch, z, c, alpha);
  vert(m, rw, z, c, alpha);
  // fin: a vertical triangle on the centre line
  vert(m, at(0.2, 0), z, c, alpha * 0.85);
  vert(m, notch, z, c, alpha * 0.85);
  vert(m, notch, z + sizeM * 0.55, c, alpha * 0.85);
}

/** Horizontal ring (ghost marker) at height z. */
export function ringMesh(m: Mesh, lon: number, lat: number, z: number, radiusM: number, widthM: number, c: Rgb, alpha: number, segments = 32): void {
  const r0 = Math.max(0, radiusM - widthM / 2);
  const r1 = radiusM + widthM / 2;
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    const p = (a: number, r: number) => offsetM(lat, lon, Math.sin(a) * r, Math.cos(a) * r);
    quad(m, p(a0, r0), z, p(a1, r0), z, p(a1, r1), z, p(a0, r1), z, c, alpha);
  }
}

/** Forecast geometry is re-anchored on a coarser clock than the frame: ahead (s) is floored to this. */
export const AHEAD_QUANTUM_S = 0.25;
export const quantizeAhead = (ahead: number): number => Math.floor(Math.max(0, ahead) / AHEAD_QUANTUM_S) * AHEAD_QUANTUM_S;

/** Cone + ribbon + time-tick rings of one aircraft's forecast (the geometry that need not be rebuilt every frame). */
export function forecastMesh(m: Mesh, forecast: readonly TrackSample[], air: number | null, ground: number, mpp: number, c: Rgb, light: boolean): void {
  if (forecast.length < 2) return;
  const coneA = light ? 0.1 : 0.09;
  if (air === null) {
    // altitude unknown: the forecast lies on the ground, honestly flat
    const zOf = () => ground;
    coneMesh(m, forecast, zOf, c, coneA);
    ribbonMesh(m, forecast, zOf, mpp * 1.3, c, 0.85);
    return;
  }
  const nowAlt = forecast[0]?.altM ?? null;
  const zOf = (q: TrackSample) => air + (q.altM != null && nowAlt != null ? q.altM - nowAlt : 0);
  coneMesh(m, forecast, zOf, c, coneA);
  ribbonMesh(m, forecast, zOf, mpp * 1.3, c, 0.85);
  for (const ts of TICK_SECONDS) {
    const q = sampleAt(forecast, ts);
    if (q) ringMesh(m, q.lon, q.lat, zOf(q), mpp * (ts === 60 ? 3.6 : 2.8), mpp * 2, c, 0.9, 12);
  }
}

/** What a forecast mesh was built from; any change beyond tolerance means it must be rebuilt. */
export interface ForecastKey {
  /** identity of the world-state snapshot (a new object per world update) */
  owner: object;
  /** dead-reckoned seconds, already quantized */
  aheadQ: number;
  /** metres per pixel (ribbon width) */
  mpp: number;
  air: number | null;
  ground: number;
  color: string;
  light: boolean;
}

/** Tracks per-aircraft forecast keys so geometry is rebuilt only on a real change (state, ~25 cm of ahead, 1% zoom, 0.5 m height, colour, theme). */
export class ForecastGate {
  private keys = new Map<string, ForecastKey>();

  /** true (and the key is remembered) when `id` needs its forecast mesh rebuilt. */
  stale(id: string, k: ForecastKey): boolean {
    const p = this.keys.get(id);
    if (
      p &&
      p.owner === k.owner &&
      p.aheadQ === k.aheadQ &&
      Math.abs(k.mpp / p.mpp - 1) <= 0.01 &&
      (p.air === null) === (k.air === null) &&
      (k.air === null || Math.round(p.air! * 2) === Math.round(k.air * 2)) &&
      Math.round(p.ground * 2) === Math.round(k.ground * 2) &&
      p.color === k.color &&
      p.light === k.light
    )
      return false;
    this.keys.set(id, k);
    return true;
  }

  forget(id: string): void {
    this.keys.delete(id);
  }

  /** Forget every id not in `keep`. */
  retain(keep: ReadonlySet<string>): void {
    for (const id of this.keys.keys()) if (!keep.has(id)) this.keys.delete(id);
  }

  clear(): void {
    this.keys.clear();
  }
}

// ───────────── height label ─────────────

/** Height above the ground (m): reported/derived AGL, else MSL − DEM ground; null when unknown or not MSL. */
export function aglMetres(s: Pick<AircraftState, 'aglFt' | 'altFt' | 'altRef' | 'groundElevM'>): number | null {
  if (s.aglFt != null && Number.isFinite(s.aglFt)) return s.aglFt * M_PER_FT;
  const msl = mslMetres(s);
  if (msl !== null && s.groundElevM != null && Number.isFinite(s.groundElevM)) return msl - s.groundElevM;
  return null;
}

/** MSL altitude (m); null when unknown or in an unconverted reference (PA / HAE). */
export function mslMetres(s: Pick<AircraftState, 'altFt' | 'altRef'>): number | null {
  return s.altFt != null && Number.isFinite(s.altFt) && (s.altRef == null || s.altRef === 'MSL') ? s.altFt * M_PER_FT : null;
}

/** 3D label line: "1,240 ft AGL" ("~" when the position is estimated); MSL when AGL is unknown; null without altitude. */
export function heightLabel(s: Pick<AircraftState, 'aglFt' | 'altFt' | 'altRef' | 'groundElevM'>, estimated: boolean): string | null {
  const agl = aglMetres(s);
  const pre = estimated ? '~' : '';
  if (agl !== null) return `${pre}${fmt.int(Math.max(0, Math.round(agl / M_PER_FT / 10) * 10))} ft AGL`;
  const msl = mslMetres(s);
  if (msl !== null) return `${pre}${fmt.int(Math.round(s.altFt! / 10) * 10)} ft MSL`;
  return null;
}
